import { FCC } from "@/types/common";
import { CaseTitle } from "@/features/case/components/CaseTitle";
import { CaseStep } from "@/features/case/components/CaseStep";
import { CaseBasicInfo } from "@/features/case/components/CaseBasicInfo/CaseBasicInfo";
import { TCaseBasicInfo } from "@/features/case/types/case";
import { CaseEditMatches } from "@/features/case/components/CaseEdit/CaseEditMatches";
import { TCaseMatches, TGetCaseDto } from "@/api/services/main/case/dtos/get-case.dto";
import { Box, Stack } from "@mui/material";
import { Button } from "@/components/elements/Button";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { Form } from "@/components/elements/Form";
import { CaseScheduleBlock } from "@/features/case/components/CaseSchedule/CaseScheduleBlock";
import { CaseComment } from "@/features/case/components/CaseComments/CaseComment";
import { CaseMemoField } from "@/features/case/components/case-common/CaseMemoField";
import { TGetJobDto } from "@/api/services/main/job/dtos/get-job.dto";
import { UseFormReturn } from "react-hook-form";
import { ObjectSchema } from "yup";
import { LoadingOverlay } from "@/features/case/components/case-common/LoadingOverlay";
import { DuplicateCaseModal } from "@/features/case/components/case-common/DuplicateCaseModal";
import { TCheckCaseDuplicateDto } from "@/api/services/main/case/dtos/check-case-duplicate.dto";
import { TGetCaseMatchesReqDto } from "@/api/services/main/case/dtos/get-case-matches.dto";
import { IconEditorListAltAdd } from "@/assets/icons/IconEditorListAltAdd";
import { JOB_STATUS } from "@/types/enum";

type CaseEditFormLayoutProps = {
  // Form methods and state
  methods: UseFormReturn<TCaseBasicInfo>;
  caseSchema: ObjectSchema<TCaseBasicInfo>;
  onSubmit: (data: TCaseBasicInfo) => Promise<void>;
  hasStatusChanges: boolean;
  hasBasicInfoChanges: boolean;
  hasChangedStatusWithMissingFields: boolean;

  // Job info
  jobInfo: TGetJobDto;

  // Case info
  caseInfo: TGetCaseDto | undefined;
  activeStep: number;
  managementNumber: string | undefined;
  caseMatchesData: TCaseMatches[];
  total: number;
  countItem: number;
  totalPage: number;

  // Status handlers
  onStatusMapChange: (newStatusMap: Record<number, number>) => void;
  onTalentStatusMapChange: (newTalentStatusMap: Record<number, number>) => void;
  onDeleteCaseMatch: (caseMatchId: number) => Promise<void>;
  onCaseMatchMissingFieldsUpdate: (updates: Record<number, boolean>) => void;

  // Edit state
  canEdit: boolean;
  isCompleted: boolean;
  isPersonInChargeLocked: boolean;
  isSubmitting: boolean;
  isSavingCaseMatchStatus: boolean;

  // Utilities
  formatTotal: (total?: number) => string;
  onNavigateBack: () => void;
  onNavigateCreateCase: () => void;
  onSaveBasicInfo: () => void;
  onAutoSaveCaseMatchStatus: (caseMatchId: number, newStatus?: number, newTalentStatus?: number) => Promise<void>;
  onAutoSaveMemo: (newMemo: string | null | undefined) => Promise<void>;

  // Filter
  caseMatchFilterParams: Partial<TGetCaseMatchesReqDto>;
  onCaseMatchFilterParamsChange: (params: Partial<TGetCaseMatchesReqDto>) => void;

  // Duplicate case modal
  duplicateCaseModalOpen: boolean;
  duplicateCases: TCheckCaseDuplicateDto[];
  pendingCaseName: string;
  onDuplicateCaseModalClose: () => void;
  jobId: number;
};

export const CaseEditFormLayout: FCC<CaseEditFormLayoutProps> = ({
  methods,
  caseSchema,
  onSubmit,
  hasStatusChanges,
  hasBasicInfoChanges,
  hasChangedStatusWithMissingFields,
  jobInfo,
  caseInfo,
  activeStep,
  managementNumber,
  caseMatchesData,
  total,
  countItem,
  totalPage,
  onStatusMapChange,
  onTalentStatusMapChange,
  onDeleteCaseMatch,
  onCaseMatchMissingFieldsUpdate,
  canEdit,
  isCompleted,
  isPersonInChargeLocked,
  isSubmitting,
  isSavingCaseMatchStatus,
  formatTotal,
  onNavigateBack,
  onNavigateCreateCase,
  onSaveBasicInfo,
  onAutoSaveCaseMatchStatus,
  onAutoSaveMemo,
  caseMatchFilterParams,
  onCaseMatchFilterParamsChange,
  duplicateCaseModalOpen,
  duplicateCases,
  pendingCaseName,
  onDuplicateCaseModalClose,
  jobId,
}) => {
  const canCreateNewCase = jobInfo?.status === JOB_STATUS.SCHEDULED || jobInfo?.status === JOB_STATUS.OPEN;

  const customLeaveMessage = hasChangedStatusWithMissingFields
    ? "マッチング情報の入力が完了しておりません。このまま戻った場合はステータスが戻されますが、よろしいですか？"
    : undefined;
  return (
    <>
      <LoadingOverlay open={isSavingCaseMatchStatus} />
      <Form methods={methods} onSubmit={onSubmit} schema={caseSchema} shouldBlock={hasStatusChanges} customMessageLeave={customLeaveMessage}>
        <CaseTitle
          jobInfo={jobInfo}
          managementNumber={managementNumber ?? caseInfo?.managementNumber}
          caseName={caseInfo?.name}
          progressStatus={caseInfo?.progressStatus}
          caseMatches={caseMatchesData}
          totalCandidates={total}
          isAllFailed={caseInfo?.isAllFailed}
          shareUrl={caseInfo?.shareUrl ?? undefined}
          sharePassword={caseInfo?.sharePassword ?? undefined}
          shareUrlExpiredAt={caseInfo?.shareUrlExpiredAt ?? undefined}
        />
        <CaseStep
          activeStep={activeStep}
          progressStatus={caseInfo?.progressStatus}
          caseMatches={caseMatchesData}
          isAllFailed={caseInfo?.isAllFailed}
        />

        <Box>
          <Stack direction="row" gap={4} sx={{ width: "100%" }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <CaseBasicInfo
                jobInfo={jobInfo}
                disabled={!canEdit}
                isCompleted={isCompleted}
                isPersonInChargeLocked={isPersonInChargeLocked}
                onSave={onSaveBasicInfo}
                isSubmitting={isSubmitting}
                hasChanges={hasBasicInfoChanges}
              />
              <CaseEditMatches
                caseMatchesData={caseMatchesData}
                total={total}
                countItem={countItem}
                totalPage={totalPage}
                formatTotal={formatTotal}
                onStatusMapChange={onStatusMapChange}
                onTalentStatusMapChange={onTalentStatusMapChange}
                onDeleteCaseMatch={onDeleteCaseMatch}
                onCaseMatchMissingFieldsUpdate={onCaseMatchMissingFieldsUpdate}
                onAutoSaveCaseMatchStatus={onAutoSaveCaseMatchStatus}
                canEdit={canEdit}
                isCompleted={isCompleted}
                caseName={caseInfo?.name}
                filterParams={caseMatchFilterParams}
                onFilterParamsChange={onCaseMatchFilterParamsChange}
              />
              <CaseMemoField control={methods.control} disabled={!canEdit} mt={4} mb={3} onBlur={onAutoSaveMemo} />
              {/* <CaseCompanyShareBlock /> */}
            </Box>
            <Box sx={{ width: "350px", flexShrink: 0 }}>
              <CaseScheduleBlock disabled={!canEdit} isCompleted={isCompleted} />
              <CaseComment disabled={!canEdit} />
            </Box>
          </Stack>
        </Box>

        <FooterContainer>
          <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" gap={2}>
              <Button variant="ghost" size="sm" onClick={onNavigateBack} sx={{ width: "130px" }}>
                案件一覧に戻る
              </Button>
            </Stack>
            <Stack direction="row" gap={2}>
              <Button variant="outline" size="sm" onClick={onNavigateCreateCase} startIcon={<IconEditorListAltAdd />} disabled={!canCreateNewCase}>
                新しい案件で再募集
              </Button>
            </Stack>
          </Stack>
        </FooterContainer>
        <DuplicateCaseModal
          open={duplicateCaseModalOpen}
          onClose={onDuplicateCaseModalClose}
          duplicateCases={duplicateCases}
          caseName={pendingCaseName}
          jobId={jobId}
        />
      </Form>
    </>
  );
};
